import type { Configurator } from '~/models/configurator/configurator.model'
import type { ConfiguratorPool } from '~/models/configurator/configuratorPool.model'
import type { ConfiguratorPoolColor } from '~/models/configurator/configuratorPoolColor.model'
import type { ConfiguratorPoolLine } from '~/models/configurator/configuratorPoolLine.model'
import type { ConfiguratorPoolSize } from '~/models/configurator/configuratorPoolSize.model'
import type { ConfiguratorPoolStair } from '~/models/configurator/configuratorPoolStair.model'

interface UseConfiguratorParams {
  configurator: Ref<Configurator>
  currentStair: Ref<ConfiguratorPoolStair | null>
  currentPoolLine: Ref<ConfiguratorPoolLine | null>
  currentColor: Ref<ConfiguratorPoolColor | null>
  currentSize: Ref<ConfiguratorPoolSize | null>

}
export function useConfigurator({
  configurator,
  currentStair,
  currentPoolLine,
  currentColor,
  currentSize,
}: UseConfiguratorParams) {
  function getUniqueById<T extends { id: number }>(array: T[]): T[] {
    return array.filter((value, index, self) => self.findIndex(v => v.id === value.id) === index)
  }

  const allStairs = computed<ConfiguratorPoolStair[]>(() => {
    return getUniqueById(configurator.value.pools.map(pool => pool.stair))
  })

  const allColors = computed<ConfiguratorPoolColor[]>(() => {
    return getUniqueById(configurator.value.pools.map(pool => pool.color))
  })

  const allSizes = computed<ConfiguratorPoolSize[]>(() => {
    return getUniqueById(configurator.value.pools.map(pool => pool.size))
  })

  function getPossiblePoolOptions({
    stair,
    poolLine,
    color,
    size,
  }: {
    stair: ConfiguratorPoolStair | null
    poolLine: ConfiguratorPoolLine | null
    color: ConfiguratorPoolColor | null
    size: ConfiguratorPoolSize | null
  }): ConfiguratorPool[] {
    const poolsWithSameLine = configurator.value.pools.filter(pool => poolLine === null || pool.poolLine.id === poolLine.id)
    const poolsWithSameStair = poolsWithSameLine.filter(pool => stair === null || pool.stair.id === stair.id)
    const poolsWithSameColor = poolsWithSameStair.filter(pool => color === null || pool.color.id === color.id)
    const poolsWithSameSize = poolsWithSameColor.filter(pool => size === null || pool.size.id === size.id)
    return poolsWithSameSize
  }

  const currentShownStairs = computed<ConfiguratorPoolStair[]>(() => {
    return getUniqueById(
      configurator.value.pools
        .filter(pool => pool.poolLine.id === currentPoolLine.value?.id)
        .map(pool => pool.stair),
    )
  })

  const currentSelectableStairs = computed<ConfiguratorPoolStair[]>(() => {
    return getUniqueById(
      getPossiblePoolOptions({
        stair: null,
        poolLine: currentPoolLine.value,
        color: currentColor.value,
        size: currentSize.value,
      }).map(pool => pool.stair),
    )
  })

  const currentShownColors = computed<ConfiguratorPoolColor[]>(() => {
    return getUniqueById(
      configurator.value.pools
        .filter(pool => pool.poolLine.id === currentPoolLine.value?.id)
        .map(pool => pool.color),
    )
  })

  const currentSelectableColors = computed<ConfiguratorPoolColor[]>(() => {
    return getUniqueById(
      getPossiblePoolOptions({
        stair: currentStair.value,
        poolLine: currentPoolLine.value,
        color: null,
        size: currentSize.value,
      }).map(pool => pool.color),
    )
  })

  const currentShownSizes = computed<ConfiguratorPoolSize[]>(() => {
    return getUniqueById(
      configurator.value.pools
        .filter(pool => pool.poolLine.id === currentPoolLine.value?.id)
        .map(pool => pool.size),
    )
  })

  const currentSelectableSizes = computed<ConfiguratorPoolSize[]>(() => {
    return getUniqueById(
      getPossiblePoolOptions({
        stair: currentStair.value,
        poolLine: currentPoolLine.value,
        color: currentColor.value,
        size: null,
      }).map(pool => pool.size),
    )
  })

  const currentPool = computed<ConfiguratorPool | null>(() => {
    return getPossiblePoolOptions({
      stair: currentStair.value,
      poolLine: currentPoolLine.value,
      color: currentColor.value,
      size: currentSize.value,
    })[0] || null
  })

  const poolLines = computed<ConfiguratorPoolLine[]>(() => {
    return getUniqueById(configurator.value.pools.map(pool => pool.poolLine))
  })

  return {
    configurator,
    poolLines,

    allStairs,
    currentShownStairs,
    currentSelectableStairs,

    allColors,
    currentShownColors,
    currentSelectableColors,

    allSizes,
    currentShownSizes,
    currentSelectableSizes,

    currentPool,
    getPossiblePoolOptions,

  }
}
